import React, { useContext, useEffect, useMemo, useState } from 'react';
import { HttpContext } from '../../context/HttpContext';
import { MeContext } from '../../context/MeContext';
import { getErrorResponsePayload } from '../../utils/ajax';
import ErrorToast from '../../common/ErrorToast';
import { useToast } from '@chakra-ui/toast';
import { useNavigate } from 'react-router';
import { debounce } from 'lodash';
import PassDownloadTemplatesViewPage from './PassDownloadTemplatesViewPage';
import PassDownloadTemplatesListContext, {
  initialPassDownloadTemplates,
} from '../../store/client/PassDownloadTemplateListContext';

function determineFooterColor(hex) {
  if (!hex) return;
  const cleanHex = hex.replace(/^#/, '');
  const intVal = parseInt(cleanHex, 16);
  const r = (intVal >> 16) & 255;
  const g = (intVal >> 8) & 255;
  const b = intVal & 255;
  const average = (r + g + b) / 3;
  return average > 127 ? 'dark' : 'light';
}

function normalizedData(data) {
  if (data?.length > 0) {
    return data.map((item) => {
      const attributes = item.attributes;
      return {
        ...item,
        name: attributes.name,
        defaultForOrg: attributes.defaultForOrg,
        headerText: attributes.headerText,
        passDownloadText: attributes.passDownloadText,
        clientWebsite: attributes.clientWebsite,
        pageTitle: attributes.pageTitle,
        skipPreview: attributes.skipPreview,
        skipFooter: attributes.skipFooter,
        logo: attributes.logo,
        faviconLogo: attributes.faviconLogo,
        logoImage: attributes.logoImageUrl,
        faviconImage: attributes.faviconImage,
        componentFillColor: attributes.componentFillColor,
        componentTextColor: attributes.componentTextColor,
        backgroundColor: attributes.backgroundColor,
        headerColor: attributes.headerColor,
        passDownloadTextColor: attributes.passDownloadTextColor,
        footerColor: determineFooterColor(attributes.backgroundColor),
        passTemplates: attributes.passTemplates,
        entities: attributes.entities,
        clientHelpLink: attributes.clientHelpLink,
        editUrl: `/pass-download-pages/edit/${item.id}`,
      };
    });
  }
  return [];
}

function PassDownloadTemplatesListContent() {
  const passDownloadTemplatesListCtx = useContext(
    PassDownloadTemplatesListContext
  );
  const toast = useToast();
  const { authAxios } = useContext(HttpContext);
  const meCtx = useContext(MeContext);
  const isOrganisationActive = meCtx?.isOrganisationActive() ?? true;
  const isSetUpConfirmed = meCtx?.isSetUpConfirmed() ?? true;
  const {
    passDownloadTemplates,
    passDownloadTemplates: {
      items,
      hasMore,
      status,
      pageIndex,
      pageSize,
      items: prev,
    },
    incrementPageIndex,
    resetPageIndex,
  } = useContext(PassDownloadTemplatesListContext);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const { filterEntity } = meCtx.state;

  useEffect(() => {
    const queryPassTemplate =
      passDownloadTemplatesListCtx.filterValues.passTemplate !== ''
        ? '&passTemplateId=' +
          passDownloadTemplatesListCtx.filterValues.passTemplate
        : '';
    const querySearch = inputSearch !== '' ? '&search=' + inputSearch : '';
    const query = `${queryPassTemplate}${querySearch}`;
    setQuery(query);
    resetPageIndex();
  }, [passDownloadTemplatesListCtx.filterValues, inputSearch]);

  useEffect(() => {
    passDownloadTemplatesListCtx.updatePassDownloadTemplates(
      initialPassDownloadTemplates
    );
    resetPageIndex();
  }, [filterEntity]);

  useEffect(() => {
    if (!isOrganisationActive || !isSetUpConfirmed) {
      return;
    }
    const getData = async () => {
      passDownloadTemplatesListCtx.updatePassDownloadTemplates({
        ...passDownloadTemplates,
        loading: true,
        status: 'pending',
      });
      try {
        const startRow = pageSize * pageIndex;
        const entityId = filterEntity?.uuid;
        const response = await authAxios.get(
          `api/v1/pass_download_templates?limit=${pageSize}&offset=${startRow}${
            entityId ? `&entityId=${entityId}` : ''
          }${query}`
        );
        const total = response?.data?.meta?.total ?? 0;
        const defaultTemplateUuid = response?.data?.meta?.default_template_uuid;
        const data = response.data?.data;
        const pageCount = Math.ceil(total / pageSize);
        const hasMore =
          pageCount === -1 ? pageIndex >= pageSize : pageIndex < pageCount - 1;
        passDownloadTemplatesListCtx.updatePassDownloadTemplates({
          ...passDownloadTemplates,
          items: pageIndex > 0 ? prev.concat(data) : data,
          total,
          defaultTemplateUuid,
          hasMore,
          pageCount,
          loading: false,
          status: 'resolved',
        });
      } catch (onError) {
        console.log(onError);
        const { code } = getErrorResponsePayload(onError);
        code !== 401 &&
          toast({
            render: (props) => (
              <ErrorToast error={onError} onClose={props.onClose} />
            ),
          });
        navigate('/pass-download-pages', { replace: true });
      }
    };

    // Add a debounce of 300 milliseconds
    const debouncedGetData = debounce(getData, 300);
    debouncedGetData();

    // Cleanup function to clear the debounce timer
    return () => debouncedGetData.cancel();
  }, [
    authAxios,
    isOrganisationActive,
    isSetUpConfirmed,
    filterEntity,
    pageIndex,
    pageSize,
    query,
  ]);

  const formattedData = useMemo(() => normalizedData(items), [items]);
  const filtersApplied =
    passDownloadTemplatesListCtx.filterValues.passTemplate !== '' ||
    inputSearch !== '';

  return (
    <PassDownloadTemplatesViewPage
      items={formattedData}
      isLoading={status === 'idle' || status === 'pending'}
      hasMore={hasMore}
      loadMore={incrementPageIndex}
      filtersApplied={filtersApplied}
      setInputSearch={setInputSearch}
    />
  );
}

export default PassDownloadTemplatesListContent;
